import { Button, Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useStartImpersonation } from '@/entities/auth';
import { useMyProfile } from '@/entities/profile';
import { SUPERADMIN_ROLE_CODE } from '@/entities/roles';
import { User } from '@/entities/users';
import { cn } from '@/shared/lib';

type Props = {
  user: User;
  className?: string;
};

/**
 * Вход в режим работы от имени выбранного пользователя.
 * Скрыт всем, кроме суперадмина, а также для собственной, заблокированной
 * и суперадминской учётной записи — такое переключение сервер всё равно отклоняет.
 */
export const UserImpersonateButton: FC<Props> = ({ user, className }) => {
  const { t } = useTranslation('users');

  const { data: profile } = useMyProfile();
  const { mutate: startImpersonation, isPending } = useStartImpersonation();

  const isSuperadmin = profile?.role.code === SUPERADMIN_ROLE_CODE;
  const isSelf = !!profile && String(profile.id) === String(user.id);
  const isTargetSuperadmin = user.role.code === SUPERADMIN_ROLE_CODE;

  if (!isSuperadmin || isSelf || isTargetSuperadmin || !user.isActive) {
    return null;
  }

  const handleClick = () => {
    startImpersonation(user.id, {
      // Перезагрузка страницы, а не переход внутри приложения: сессия стала сессией
      // другого пользователя, и права, профиль и настройки интерфейса нужно перечитать с нуля
      onSuccess: () => {
        window.location.href = '/';
      },
    });
  };

  return (
    // Ширины карточки пользователя хватает не всегда: в узком блоке кнопка уходит под
    // пояснение, иначе заголовку и кнопке не хватает места в одной строке
    <div className={cn('@container/impersonate', className)}>
      <Item variant={'outline'}>
        <ItemContent>
          <ItemTitle>{t('impersonateTitle')}</ItemTitle>
          {/* Пояснение нужно целиком: под ограничением по умолчанию в узкой карточке
              пропадала фраза о том, как вернуться в свою учётную запись */}
          <ItemDescription className={'line-clamp-none'}>{t('impersonateDescription')}</ItemDescription>
        </ItemContent>
        <ItemActions className={'basis-full @xl/impersonate:basis-auto @xl/impersonate:ml-auto'}>
          <Button variant={'secondary'} isLoading={isPending} onClick={handleClick}>
            {t('impersonate')}
          </Button>
        </ItemActions>
      </Item>
    </div>
  );
};
